import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Slide,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SignIn from './SignIn';
import SignUp from './SignUp';
import { isAuthenticated } from '../../utils/authUtils';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import StarIcon from '@mui/icons-material/Star';
import DiamondIcon from '@mui/icons-material/Diamond';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import { useLanguage } from '../../utils/LanguageContext';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AuthPage = () => {
  const navigate = useNavigate();
  const [isSignIn, setIsSignIn] = useState(true);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const { language } = useLanguage();
  const hasReferral = !!localStorage.getItem('rumahkira_referral_code');

  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/app');
    }
  }, [navigate]);

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: 'RM0',
      description: 'Basic estimation',
      icon: <StarIcon />,
      features: [
        'Basic budget estimate',
        'Price range & cost/sqft',
        'One calculation only',
      ],
    },
    {
      id: 'premium',
      name: 'Premium',
      price: 'RM7',
      priceLabel: '/project',
      description: 'Detailed insights',
      icon: <DiamondIcon />,
      popular: true,
      features: [
        'Detailed breakdown',
        'Material Estimator',
        'Compare scenarios',
        'Export PDF report',
        'Save project',
      ],
    },
    {
      id: 'monthly',
      name: 'Monthly',
      price: 'RM10',
      priceLabel: '/month',
      description: 'Unlimited access',
      icon: <WorkspacePremiumIcon />,
      features: [
        'All Premium features',
        'Unlimited calculations',
        'Save multiple projects',
        'Priority support',
      ],
    },
  ];

  const handleSelectPlan = (planId) => {
    setSelectedPlan(planId);
    setPricingOpen(false);
    setIsSignIn(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 3,
      }}
    >
      {/* Logo */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
        <HomeWorkIcon color="primary" sx={{ fontSize: 36 }} />
        <Box>
          <Typography variant="h5" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.2 }}>
            RumahKira<sup style={{ fontSize: '0.5em' }}>&trade;</sup>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            by Kondingau Community Insight
          </Typography>
        </Box>
      </Box>

      {/* Referral Welcome Banner */}
      {hasReferral && (
        <Card sx={{
          width: '100%',
          maxWidth: 400,
          mb: 2,
          background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
          color: 'white',
        }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CardGiftcardIcon sx={{ fontSize: 28 }} />
            <Typography variant="body2" fontWeight={500}>
              {language === 'my'
                ? 'Anda dijemput oleh rakan! Daftar untuk dapat Pas Perancangan 3 hari percuma.'
                : "You were invited by a friend! Sign up to get a free 3-day Planning Pass."}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Auth Card */}
      <Card sx={{ width: '100%', maxWidth: 400 }}>
        <CardContent sx={{ p: 3 }}>
          {/* Selected Plan Badge */}
          {selectedPlan && !isSignIn && (
            <Chip
              label={`${plans.find(p => p.id === selectedPlan)?.name} Plan`}
              color="primary"
              size="small"
              sx={{ mb: 2 }}
            />
          )}

          {isSignIn ? (
            <SignIn onToggleForm={() => setIsSignIn(false)} />
          ) : (
            <SignUp onToggleForm={() => setIsSignIn(true)} />
          )}
        </CardContent>
      </Card>

      {/* View Pricing Link */}
      <Button
        variant="text"
        size="small"
        onClick={() => setPricingOpen(true)}
        sx={{ mt: 2 }}
      >
        View Pricing Plans
      </Button>

      {/* Pricing Dialog (Toast Style) */}
      <Dialog
        open={pricingOpen}
        onClose={() => setPricingOpen(false)}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            position: 'fixed',
            bottom: 0,
            m: 0,
            borderRadius: '16px 16px 0 0',
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={600}>
            Choose a Plan
          </Typography>
          <IconButton onClick={() => setPricingOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {plans.map((plan) => (
              <Card
                key={plan.id}
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                  border: selectedPlan === plan.id ? 2 : 1,
                  borderColor: selectedPlan === plan.id ? 'primary.main' : 'divider',
                  '&:hover': { borderColor: 'primary.main' },
                }}
                onClick={() => handleSelectPlan(plan.id)}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ color: 'primary.main' }}>{plan.icon}</Box>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {plan.name}
                          </Typography>
                          {plan.popular && (
                            <Chip label="Popular" size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {plan.description}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h6" fontWeight={700} color="primary">
                        {plan.price}
                      </Typography>
                      {plan.priceLabel && (
                        <Typography variant="caption" color="text.secondary">
                          {plan.priceLabel}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <List dense sx={{ mt: 1 }}>
                    {plan.features.map((feature, index) => (
                      <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            ))}
          </Box>

          <Typography variant="caption" display="block" align="center" color="text.secondary" sx={{ mt: 2 }}>
            All estimates are for planning purposes only
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AuthPage;
